/**
 * Backend audit-log API — read hook.
 *
 * Tenant-scoped query against the WORM-paired Cosmos index. The handler
 * filters out `fic.*` events for non-FIC-authorised viewers per
 * FICA s38 (c2-pass2-07).
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from './client';

export interface AuditLogView {
  id: string;
  tenantId: string;
  action: string;
  subjectType: string;
  subjectId: string;
  subjectIdHash: string;
  subjectIdHashKeyId: string;
  performedByRole: string;
  correlationId?: string;
  metadata: Record<string, unknown>;
  prevHash: string;
  hash: string;
  ts: string;
  createdAt: string;
}

export interface AuditLogListResponse {
  events: AuditLogView[];
  /** True when the window was cut short — more events exist than were
   *  returned (server over-fetch detection, 2026-08-22). Absent on older
   *  responses; treat absent as unknown, not as complete. */
  truncated?: boolean;
  /** Opaque handle for the next page (B4, 2026-08-23). Pass it back as
   *  `cursor` to continue.
   *
   *  ⚠ ABSENT on deploys that predate cursor support — that is exactly the
   *  case the `truncated` banner still exists to cover. `null` means the end
   *  of the trail; `undefined` means this server cannot page and `truncated`
   *  is all the caller gets. Do not collapse the two. */
  nextCursor?: string | null;
  correlationId: string;
}

export interface UseAuditLogOptions {
  actions?: string[];
  subjectTypes?: string[];
  /** Filter to events whose `performedByRole` matches (#1 — a real row field).
   *  NOTE: audit rows carry no `status` field, so the search UI offers `role`
   *  (this) but never a status filter. */
  role?: string;
  since?: string;
  /** ISO upper bound for the date-range UI (#1). */
  until?: string;
  limit?: number;
  /** Opaque cursor from the previous page's `nextCursor`. Matches the
   *  `useInvoices` idiom. Most callers want `useAuditLogPages` instead. */
  cursor?: string;
  enabled?: boolean;
}

function buildPath(opts: UseAuditLogOptions): string {
  const search = new URLSearchParams();
  if (opts.actions && opts.actions.length > 0) {
    for (const a of opts.actions) search.append('action', a);
  }
  if (opts.subjectTypes && opts.subjectTypes.length > 0) {
    for (const s of opts.subjectTypes) search.append('subjectType', s);
  }
  if (opts.role) search.set('role', opts.role);
  if (opts.since) search.set('since', opts.since);
  if (opts.until) search.set('until', opts.until);
  if (opts.limit !== undefined) search.set('limit', String(opts.limit));
  if (opts.cursor) search.set('cursor', opts.cursor);
  const qs = search.toString();
  return qs ? `/api/v1/audit-log?${qs}` : '/api/v1/audit-log';
}

export function useAuditLog(opts: UseAuditLogOptions = {}) {
  return useQuery<AuditLogListResponse>({
    queryKey: ['audit-log', opts],
    queryFn: ({ signal }) =>
      api<AuditLogListResponse>(buildPath(opts), { signal }),
    enabled: opts.enabled ?? true,
  });
}

/**
 * Paginated audit log — follows the server's `nextCursor` so a caller can
 * reach the WHOLE trail, not just the newest page.
 *
 * Use this anywhere a user reads the audit log as a record. `useAuditLog`
 * stays for the fixed-window readers (dashboard activity card, the privacy
 * CSV snapshot) that deliberately want one bounded page.
 *
 * ⚠ Page on the CURSOR, never on `events.length`. The server applies the FICA
 * s38 tipping-off filter AFTER paging, so a page whose rows are all `fic.*`
 * arrives EMPTY for an unauthorised viewer while the trail continues. Stopping
 * at the first empty page would silently truncate the record.
 *
 * ⚠ Degrades on deploys that predate cursor support: those omit `nextCursor`
 * entirely, `hasNextPage` stays false, and the caller must keep showing the
 * `truncated` banner. That is why the banner is not simply replaced.
 */
export function useAuditLogPages(opts: UseAuditLogOptions = {}) {
  return useInfiniteQuery<
    AuditLogListResponse,
    Error,
    { pages: AuditLogListResponse[]; pageParams: (string | null)[] },
    readonly unknown[],
    string | null
  >({
    queryKey: ['audit-log', 'pages', opts],
    initialPageParam: null,
    queryFn: ({ pageParam, signal }) =>
      api<AuditLogListResponse>(
        buildPath({ ...opts, ...(pageParam ? { cursor: pageParam } : {}) }),
        { signal },
      ),
    // Returning undefined tells react-query there is no next page. `null`
    // (end of trail) and `undefined` (server cannot page) both land here and
    // both correctly mean "no Load more" — but only the first also means the
    // record is complete, which is why `truncated` is still read separately.
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: opts.enabled ?? true,
  });
}
